/**
 * streamRunner — the wire-to-machine translation layer.
 *
 * One instance per app, held in DI context. `start(chatId)` opens a transport
 * subscription, `stop(chatId)` closes it; both idempotent. It only LISTENS —
 * the POST (via CommandBus) is what makes the backend stream.
 */

import { STREAM_IDLE_TIMEOUT_MS } from 'src/content/ChatV2/constants';
import { CHAT_EVENT } from 'src/content/ChatV2/enums';
import {
  parseRunError,
  parseTransportError,
  streamTimeoutError
} from 'src/content/ChatV2/lib/errors';
import {
  CHAT_STREAM_EVENT,
  isStreamingState
} from 'src/content/ChatV2/machines/actions';
import type { ChatStreamEvent } from 'src/content/ChatV2/machines/chatStreamMachine';
import type {
  ChatStreamActor,
  MachineRegistry
} from 'src/content/ChatV2/machines/machineRegistry';
import { LC_RUN_PREFIX } from 'src/content/ChatV2/steps/model/stepKey';
import type { ParsedEventType } from 'src/content/ChatV2/steps/wire/eventType';
import {
  EVENT_DOMAIN,
  parseEventType,
  STEP_PHASE
} from 'src/content/ChatV2/steps/wire/eventType';
import type {
  ChatTransport,
  Unsubscribe
} from 'src/content/ChatV2/transport/ChatTransport';
import type { PubsubEventPayload } from 'src/content/ChatV2/transport/eventSchema';
import type { ChatId } from 'src/content/ChatV2/types/ids';
import { isClientSessionKey } from 'src/content/ChatV2/utils';

import type { ChatStreamRegistry } from './ChatStreamRegistry';
import { ownsFrame } from './frameOwnership';

function dataString(
  data: Record<string, unknown> | undefined,
  key: string
): string | undefined {
  const value = data?.[key];
  return typeof value === 'string' ? value : undefined;
}

function translate(
  event: PubsubEventPayload,
  parsed: ParsedEventType
): ChatStreamEvent | null {
  const data = event.data as Record<string, unknown> | undefined;

  if (parsed.domain === EVENT_DOMAIN.RUN) {
    switch (parsed.phaseRaw) {
      case 'completed':
        return {
          type: CHAT_STREAM_EVENT.STREAM_DONE,
          meta: (data ?? {}) as never
        };
      case 'error':
        return {
          type: CHAT_STREAM_EVENT.STREAM_ERROR,
          error: parseRunError(data ?? event)
        };
      case 'cancelled':
        return { type: CHAT_STREAM_EVENT.STREAM_CANCELLED };
      case 'accepted':
      case 'started':
        return null;
      default:
        return null;
    }
  }

  if (parsed.domain === EVENT_DOMAIN.MESSAGE) {
    if (parsed.phase !== STEP_PHASE.DELTA) {
      return null;
    }
    const text = dataString(data, 'content');
    return text ? { type: CHAT_STREAM_EVENT.STREAM_CHUNK, text } : null;
  }

  return null;
}

export class StreamRunner {
  private readonly subscriptions = new Map<ChatId, Unsubscribe>();
  private readonly activeTextRun = new Map<ChatId, string>();

  private readonly watchdogs = new Map<ChatId, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly transport: ChatTransport,
    private readonly machineRegistry: MachineRegistry,
    private readonly streamRegistry?: ChatStreamRegistry
  ) {}

  start(chatId: ChatId): void {
    if (this.subscriptions.has(chatId)) {
      return;
    }
    const actor = this.machineRegistry.get(chatId);
    const unsubscribe = this.transport.subscribe(chatId, (event) => {
      this.forward(chatId, actor, event);
    });
    this.subscriptions.set(chatId, unsubscribe);
  }

  noteTurnStarted(chatId: ChatId): void {
    this.armWatchdog(chatId);
  }

  stop(chatId: ChatId): void {
    this.disarmWatchdog(chatId);
    const unsubscribe = this.subscriptions.get(chatId);
    if (!unsubscribe) {
      return;
    }
    unsubscribe();
    this.subscriptions.delete(chatId);
    this.activeTextRun.delete(chatId);
  }

  stopAll(): void {
    for (const unsubscribe of this.subscriptions.values()) {
      unsubscribe();
    }
    this.subscriptions.clear();
    this.activeTextRun.clear();
    for (const chatId of [...this.watchdogs.keys()]) {
      this.disarmWatchdog(chatId);
    }
  }

  activeChatIds(): ChatId[] {
    return [...this.subscriptions.keys()];
  }

  private forward(
    chatId: ChatId,
    actor: ChatStreamActor,
    event: PubsubEventPayload
  ): void {
    const eventType = event.event_type ?? event.event;
    if (eventType === CHAT_EVENT.ERROR) {
      if (this.ownsEvent(chatId, event)) {
        this.forwardTransportError(chatId, actor, event);
      }
      return;
    }

    const parsed = parseEventType(eventType);
    if (!parsed.prefixed) {
      return;
    }

    if (!this.ownsEvent(chatId, event)) {
      return;
    }

    this.noteFrame(chatId, parsed);

    this.streamRegistry?.get(chatId).stepStore.ingest(event);

    this.scopeBufferToRun(chatId, event, parsed);

    if (
      parsed.domain === EVENT_DOMAIN.INTERACTION &&
      parsed.phaseRaw === 'required'
    ) {
      actor.send({
        type: CHAT_STREAM_EVENT.STREAM_DONE,
        meta: this.interruptSettleMeta(chatId, event) as never
      });
      return;
    }

    const fsmEvent = translate(event, parsed);
    if (fsmEvent) {
      actor.send(fsmEvent);
    }
  }

  /**
   * The chat backend's `error` frame — terminal for the turn, like
   * `io.doclm.run.error`, but raised by the backend rather than doclm (e.g. the
   * response could not be delivered over the socket). Ownership is checked by
   * `forward` before this is called.
   */
  private forwardTransportError(
    chatId: ChatId,
    actor: ChatStreamActor,
    event: PubsubEventPayload
  ): void {
    this.disarmWatchdog(chatId);
    this.activeTextRun.delete(chatId);
    actor.send({
      type: CHAT_STREAM_EVENT.STREAM_ERROR,
      error: parseTransportError(event)
    });
  }

  /** Does this frame belong to this chat? One socket serves every chat. */
  private ownsEvent(chatId: ChatId, event: PubsubEventPayload): boolean {
    const frameChatId =
      event.chat_id === undefined ? undefined : String(event.chat_id);
    return ownsFrame({
      frameChatId,
      myServerId: this.expectedChatIdFor(chatId),
      claimedByAnother: () =>
        frameChatId !== undefined &&
        this.isClaimedByAnotherChat(chatId, frameChatId)
    });
  }

  private expectedChatIdFor(chatId: ChatId): string | undefined {
    const known = this.machineRegistry.get(chatId).getSnapshot()
      .context.serverChatId;
    if (known !== undefined) {
      return String(known);
    }
    return isClientSessionKey(chatId) ? undefined : String(chatId);
  }

  private isClaimedByAnotherChat(self: ChatId, frameChatId: string): boolean {
    for (const other of this.subscriptions.keys()) {
      if (other === self) {
        continue;
      }
      if (this.expectedChatIdFor(other) === frameChatId) {
        return true;
      }
    }
    return false;
  }

  private noteFrame(
    chatId: ChatId,
    parsed: ReturnType<typeof parseEventType>
  ): void {
    const isRunTerminal =
      parsed.domain === EVENT_DOMAIN.RUN &&
      (parsed.phaseRaw === 'completed' ||
        parsed.phaseRaw === 'error' ||
        parsed.phaseRaw === 'cancelled');
    const isInterrupt =
      parsed.domain === EVENT_DOMAIN.INTERACTION &&
      parsed.phaseRaw === 'required';

    if (isRunTerminal || isInterrupt) {
      this.disarmWatchdog(chatId);
      return;
    }
    if (
      !isStreamingState(
        this.machineRegistry.get(chatId).getSnapshot().value as string
      )
    ) {
      return;
    }
    this.armWatchdog(chatId);
  }

  private armWatchdog(chatId: ChatId): void {
    this.disarmWatchdog(chatId);
    this.watchdogs.set(
      chatId,
      setTimeout(() => this.onIdleTimeout(chatId), STREAM_IDLE_TIMEOUT_MS)
    );
  }

  private disarmWatchdog(chatId: ChatId): void {
    const timer = this.watchdogs.get(chatId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.watchdogs.delete(chatId);
    }
  }

  private onIdleTimeout(chatId: ChatId): void {
    this.watchdogs.delete(chatId);
    const actor = this.machineRegistry.get(chatId);
    if (!isStreamingState(actor.getSnapshot().value as string)) {
      return;
    }
    actor.send({
      type: CHAT_STREAM_EVENT.STREAM_ERROR,
      error: streamTimeoutError()
    });
  }

  private interruptSettleMeta(
    chatId: ChatId,
    event: PubsubEventPayload
  ): Record<string, unknown> {
    const snapshot = this.streamRegistry?.get(chatId).stepStore.getSnapshot();
    const narrative: Record<string, unknown>[] = [];

    for (const key of snapshot?.order ?? []) {
      const step = snapshot?.byKey.get(key);
      if (step?.domain === EVENT_DOMAIN.MESSAGE && step.outputText) {
        narrative.push({
          type: 'ai',
          ...(step.runId && { id: `${LC_RUN_PREFIX}${step.runId}` }),
          content: step.outputText
        });
      }
    }

    narrative.push((event.data as Record<string, unknown>) ?? {});

    return { additional_steps: narrative };
  }

  private scopeBufferToRun(
    chatId: ChatId,
    event: PubsubEventPayload,
    parsed: ParsedEventType
  ): void {
    const data = event.data as Record<string, unknown> | undefined;

    if (parsed.domain === EVENT_DOMAIN.RUN) {
      if (parsed.phaseRaw !== 'accepted' && parsed.phaseRaw !== 'started') {
        this.activeTextRun.delete(chatId);
      }
      return;
    }

    if (
      parsed.domain !== EVENT_DOMAIN.MESSAGE ||
      parsed.phase !== STEP_PHASE.DELTA
    ) {
      return;
    }
    if (!dataString(data, 'content')) {
      return;
    }
    const runId = dataString(data, 'run_id');
    if (!runId) {
      return;
    }

    const previous = this.activeTextRun.get(chatId);
    if (previous === runId) {
      return;
    }
    if (previous !== undefined) {
      this.streamRegistry?.get(chatId).buffer.reset();
    }
    this.activeTextRun.set(chatId, runId);
  }
}

export { translate as __translateForTests };
